import type { dynamicObj } from 'src/pages/assets';

import {
  Formatter as Acl_Formatter,
  Cache as Acl_Cache,
  USER_GROUP_IDS_KEY,
} from 'src/core/acls/index';
import { logger } from 'src/utils/logger';
import { useDocumentStore, useSettingStore } from 'src/store/main';
import { getUserDetailFromkey, isUserAdmin } from 'src/utils/UserDetails';
import { group_permission_activated } from 'src/constants';
import { toBoolPreference } from 'src/utils/preferenceBool';
import { retryWithBackoff } from 'src/utils/retryWithBackoff';
import { hasValidOrgId } from 'src/utils/organization/org_id_helper';

// Document-level permission values (List.vue - who can perform actions)
// Encoded as a bitmask so OWNER and ASSIGNEE can be combined.
export const PermissionLevel = {
  NO_ACCESS: 0,         // No permission to perform action
  FULL: 1,              // Everyone can perform action
  OWNER: 2,             // Only the document creator
  ASSIGNEE: 4,          // Only the assigned user/group
  OWNER_ASSIGNEE: 6,    // Owner OR assignee (OWNER | ASSIGNEE)
} as const;

export type PermissionLevelType = typeof PermissionLevel[keyof typeof PermissionLevel];

// Field-level permission values (Detail.vue - what access to fields)
export const FieldPermission = {
  NO_ACCESS: 0,   // Field is hidden/inaccessible
  READ_ONLY: 1,   // Field is visible but not editable
  READ_WRITE: 2,  // Field is visible and editable
  APPROVAL: 16,   // Field requires approval to edit
} as const;

export type FieldPermissionType = typeof FieldPermission[keyof typeof FieldPermission];

// Access status for document types
export const AccessStatus = {
  DISABLED: 0,  // access = 0 or undefined means disabled
  ENABLED: 1,   // access = 1 means enabled
} as const;

// In-flight fetch dedup (2026-05-07): on fresh login both
// PrepareDashboard and dataCache fire `Acl.fetch()` within ~42ms.
// Both pass the empty-cache guard before either populates the cache,
// so two concurrent /access_controls/get_doc_type_permissions calls
// hit the dev API simultaneously, doubling worker pressure and
// adding seconds to user-perceived login.  Mirroring the
// `settingReqPromise` pattern in setting.store.ts: the second
// caller awaits the first's in-flight promise instead of starting
// a parallel request.
let _inflightFetch: Promise<void> | null = null;

export const Acl = {
  /**
   * Check if user has access to perform an action on a document
   * @param action - The action to check (list, view, edit, delete, mass_update, first_approval, second_approval, unlock_document)
   * @param document - Optional document object. Owner check reads `created_by`
   *   (legacy `created_by_id` accepted); assignee check reads `assigned_to`
   *   (legacy `assigned_to_id` accepted).
   * @returns boolean - true if user has access
   */
  hasAccess(action: string, document = {} as dynamicObj): boolean {
    try {
      const doc_store = useDocumentStore();
      const { record } = doc_store;
      const user_id = getUserDetailFromkey('id');

      // Use provided document or fall back to store record
      const doc = document.doc_type ? document : record;
      const document_key = doc.doc_type || '';

      // Exception: NOT_ENOUGH_PERMISSIONS - allow if no document context
      if (!document_key || !action) {
        return true;
      }

      const acls = Acl_Cache.get() || {};

      // By-pass for admin or no permissions configured
      if (isUserAdmin() || Object.keys(acls).length === 0) {
        return true;
      }

      // By-pass if access control settings are not enabled
      const settings_store = useSettingStore();
      if (!toBoolPreference(settings_store.user_settings[group_permission_activated])) {
        return true;
      }

      // No permissions defined for this document type - deny access
      if (!(document_key in acls)) {
        return false;
      }

      const doc_permissions = acls[document_key];

      // Check if document type access is enabled via 'access' field if it exists
      // access = 1 means enabled, access = 0 or undefined means disabled
      // Note: New API may not have 'access' field - skip this check if not present
      if ('access' in doc_permissions && doc_permissions.access !== AccessStatus.ENABLED) {
        return false;
      }

      // By-pass if action is not set for doc type
      if (!(action in doc_permissions)) {
        return true;
      }

      const permissionValue = doc_permissions[action];

      // Handle 0 value (no access)
      if (permissionValue === PermissionLevel.NO_ACCESS) {
        return false;
      }

      // Handle granular permission levels (1=Full, 2=Owner, 4=Assignee, 6=Owner|Assignee)
      return this.checkPermissionLevel(permissionValue, doc, user_id);
    } catch (err) {
      logger.error('[ACL] hasAccess error:', err);
    }
    return false;
  },

  /**
   * Check permission based on level (Full / Owner / Assignee / Owner+Assignee).
   * OWNER (2) and ASSIGNEE (4) are bit flags — value 6 means owner OR assignee.
   */
  checkPermissionLevel(
    permissionValue: number | boolean,
    document: dynamicObj,
    userId: string
  ): boolean {
    // Legacy boolean support
    if (typeof permissionValue === 'boolean') {
      return permissionValue;
    }

    if (permissionValue === PermissionLevel.NO_ACCESS) {
      return false;
    }

    if (permissionValue === PermissionLevel.FULL) {
      return true;
    }

    const KNOWN_ROLE_MASK =
      PermissionLevel.OWNER |
      PermissionLevel.ASSIGNEE |
      PermissionLevel.OWNER_ASSIGNEE;

    // Only treat the value as a role bitmask if it consists solely of known role bits.
    if (permissionValue > 0 && (permissionValue & ~KNOWN_ROLE_MASK) === 0) {
      // Canonical field is `created_by` (the uploader). `created_by_id` is a
      // legacy alias retained for any callers/fixtures still using it.
      const isOwner = document.created_by == userId || document.created_by_id == userId;
      // Assignment is either to a single user or to a group:
      //  - User: `assigned_to_id` (canonical) / `assigned_to` (legacy alias)
      //    holds the user id. The user is the assignee when it matches.
      //  - Group: `assigned_to_group_id` holds the group id and `assigned_to_id`
      //    is null. The user is the assignee when they belong to that group.
      const isAssignee =
        this._isAssignedUser(document, userId) ||
        this._isAssignedGroupMember(document);

      if ((permissionValue & PermissionLevel.OWNER) && isOwner) {
        return true;
      }

      if ((permissionValue & PermissionLevel.ASSIGNEE) && isAssignee) {
        return true;
      }

      return false;
    }

    // Unknown value - default to allowed for backwards compatibility
    return true;
  },

  /**
   * The ids of the groups the current user belongs to. Populated by
   * fetchUserGroups() and cached alongside the ACL permissions. Read
   * synchronously by the assignee check, so it must be pre-loaded on login.
   */
  _getUserGroupIds(): string[] {
    try {
      const acls = Acl_Cache.get() || {};
      const ids = acls[USER_GROUP_IDS_KEY];
      return Array.isArray(ids) ? ids : [];
    } catch (err) {
      logger.error('[ACL] _getUserGroupIds error:', err);
    }
    return [];
  },

  /**
   * Fetch the groups the current user is a member of and cache their ids
   * alongside the ACL permissions, so the (synchronous) assignee check can
   * recognise the user as the assignee of a group-assigned document.
   *
   * Uses getUserGroups() (/groups_and_users/get_user_groups) — NOT the ACL
   * endpoint, which only returns groups that have doc-type permissions
   * configured rather than the user's actual memberships.
   *
   * @param force - If true, re-fetch even when group ids are already cached.
   */
  async fetchUserGroups(force = false) {
    try {
      const cached = Acl_Cache.get();
      if (!force && cached && Array.isArray(cached[USER_GROUP_IDS_KEY])) {
        return;
      }

      const { documentPermissionsApiService } = await import('src/services');
      const response = await documentPermissionsApiService.getUserGroups();
      const groupIds = Array.isArray(response)
        ? response
            .map((group: { id?: string }) => group?.id)
            .filter((id: string | undefined): id is string => Boolean(id))
        : [];

      // Merge into the existing cache without clobbering the permissions.
      const acls = Acl_Cache.get() || {};
      acls[USER_GROUP_IDS_KEY] = groupIds;
      Acl_Cache.save(acls);
      logger.debug('[ACL] User groups fetched and cached');
    } catch (err) {
      logger.error('[ACL] fetchUserGroups error:', err);
    }
  },

  /**
   * True when the document is assigned to the given user.
   * Canonical field is `assigned_to_id`; `assigned_to` is a legacy alias still
   * surfaced by some flows (e.g. dashboard restart). When the document is
   * assigned to a group, `assigned_to` holds the group name (not the user id),
   * so it only matches when the assignment is to a single user.
   */
  _isAssignedUser(document: dynamicObj, userId: string): boolean {
    if (!userId) return false;
    return document.assigned_to_id == userId || document.assigned_to == userId;
  },

  /**
   * True when the document is assigned to a group the current user belongs to.
   * Group assignment is indicated by `assigned_to_group_id` (with
   * `assigned_to_id` null and `assigned_to` holding the group name).
   */
  _isAssignedGroupMember(document: dynamicObj): boolean {
    const groupId = document.assigned_to_group_id;
    if (!groupId) return false;
    return this._getUserGroupIds().includes(groupId);
  },

  /**
   * Check if user has access to a specific field
   * Field permission values: 0=NoAccess, 1=ReadOnly, 2=ReadWrite, 16=Approval
   *
   * @param documentKey - The document type key
   * @param fieldId - The field ID (from field settings)
   * @param action - The action (view, edit)
   * @returns boolean - true if user has access to the field
   */
  hasFieldAccess(
    documentKey: string,
    fieldId: string,
    action: 'view' | 'edit' = 'view'
  ): boolean {
    try {
      const acls = Acl_Cache.get() || {};

      // By-pass for admin or no permissions configured
      if (isUserAdmin() || Object.keys(acls).length === 0) {
        return true;
      }

      // By-pass if access control settings are not enabled
      const settings_store = useSettingStore();
      if (!toBoolPreference(settings_store.user_settings[group_permission_activated])) {
        return true;
      }

      // No permissions for this document type
      if (!(documentKey in acls)) {
        return true;
      }

      const doc_permissions = acls[documentKey];

      // Check if document type access is enabled via 'access' field if it exists
      // Note: New API may not have 'access' field - skip this check if not present
      if ('access' in doc_permissions && doc_permissions.access !== AccessStatus.ENABLED) {
        return false;
      }

      // No field permissions defined - check both 'fields' and 'field_access' for backwards compatibility
      const fieldPermissions = doc_permissions.field_access || doc_permissions.fields;
      if (!fieldPermissions || fieldPermissions[fieldId] === undefined) {
        return true;
      }

      const fieldPermissionValue = fieldPermissions[fieldId];

      // Check based on field permission value
      return this.checkFieldPermission(fieldPermissionValue, action);
    } catch (err) {
      logger.error('[ACL] hasFieldAccess error:', err);
    }
    return false;
  },

  /**
   * Check field permission based on value and action
   * @param permissionValue - Field permission value (0=NoAccess, 1=ReadOnly, 2=ReadWrite, 16=Approval)
   * @param action - The action (view, edit)
   */
  checkFieldPermission(permissionValue: number, action: 'view' | 'edit'): boolean {
    switch (permissionValue) {
      case FieldPermission.NO_ACCESS:
        // No access at all
        return false;

      case FieldPermission.READ_ONLY:
        // Can view but not edit
        return action === 'view';

      case FieldPermission.READ_WRITE:
        // Full access
        return true;

      case FieldPermission.APPROVAL:
        // Can view, edit requires approval workflow
        // For basic access check, treat as read-write (approval handled elsewhere)
        return true;

      default:
        // Unknown value - default to allowed for backwards compatibility
        return true;
    }
  },

// ============================================
  // Internal helpers for field/column permissions
  // ============================================

  /** Get permission level from a specific ACL property */
  _getPermissionLevel(docType: string, itemId: string, accessKey: string): number | undefined {
    try {
      const acls = Acl_Cache.get() || {};
      if (!(docType in acls)) return undefined;

      const doc_permissions = acls[docType];
      // For field_access, also check 'fields' for backwards compatibility
      const permissions = accessKey === 'field_access'
        ? (doc_permissions.field_access || doc_permissions.fields)
        : doc_permissions[accessKey];

      return permissions?.[itemId];
    } catch (err) {
      logger.error(`[ACL] _getPermissionLevel error:`, err);
    }
    return undefined;
  },

  /** Check if permission level meets required level (hierarchy check) */
  _hasPermission(docType: string, itemId: string, requiredPermission: FieldPermissionType, accessKey: string): boolean {
    if (!itemId || !docType) return true;

    const permissionLevel = this._getPermissionLevel(docType, itemId, accessKey);
    if (permissionLevel === undefined) return true;

    const hierarchy: Record<number, number> = {
      [FieldPermission.NO_ACCESS]: 0,
      [FieldPermission.READ_ONLY]: 1,
      [FieldPermission.READ_WRITE]: 2,
      [FieldPermission.APPROVAL]: 3,
    };

    return (hierarchy[permissionLevel] ?? 0) >= (hierarchy[requiredPermission] ?? 0);
  },

  /** Check if permission level is exactly the specified value */
  _isPermission(docType: string, itemId: string, permission: FieldPermissionType, accessKey: string): boolean {
    if (!itemId || !docType) return false;
    return this._getPermissionLevel(docType, itemId, accessKey) === permission;
  },

  // ============================================
  // Public Field Permission Methods
  // ============================================

  hasFieldPermission(docType: string, fieldId: string, requiredPermission: FieldPermissionType): boolean {
    return this._hasPermission(docType, fieldId, requiredPermission, 'field_access');
  },

  isFieldPermission(docType: string, fieldId: string, permission: FieldPermissionType): boolean {
    return this._isPermission(docType, fieldId, permission, 'field_access');
  },

  /** Get the permission level for a specific field */
  getFieldPermissionLevel(docType: string, fieldId: string): number | undefined {
    return this._getPermissionLevel(docType, fieldId, 'field_access');
  },

  /** Get all field permissions for a document type */
  getFieldPermissions(documentKey: string): dynamicObj {
    try {
      const acls = Acl_Cache.get() || {};
      if (!(documentKey in acls)) return {};

      const doc_permissions = acls[documentKey];
      return doc_permissions.field_access || doc_permissions.fields || {};
    } catch (err) {
      logger.error('[ACL] getFieldPermissions error:', err);
    }
    return {};
  },

  // ============================================
  // Public Column Permission Methods
  // ============================================

  hasColumnPermission(docType: string, columnId: string, requiredPermission: FieldPermissionType): boolean {
    return this._hasPermission(docType, columnId, requiredPermission, 'table_column_access');
  },

  isColumnPermission(docType: string, columnId: string, permission: FieldPermissionType): boolean {
    return this._isPermission(docType, columnId, permission, 'table_column_access');
  },

  /**
   * Check if a document type is accessible (not disabled)
   * @param documentKey - The document type key
   * @returns boolean
   */
  isDocumentTypeAccessible(documentKey: string): boolean {
    try {
      const acls = Acl_Cache.get() || {};

      // By-pass for admin or no permissions configured
      if (isUserAdmin() || Object.keys(acls).length === 0) {
        return true;
      }

      // By-pass if access control settings are not enabled
      const settings_store = useSettingStore();
      if (!toBoolPreference(settings_store.user_settings[group_permission_activated])) {
        return true;
      }

      if (!(documentKey in acls)) {
        return false;
      }

      // If 'access' field exists, check it (access = 0 means enabled)
      // If 'access' field doesn't exist (new API format), document type is accessible
      // if it exists in the permissions object
      const doc_permissions = acls[documentKey];
      if ('access' in doc_permissions) {
        return doc_permissions.access === AccessStatus.ENABLED;
      }

      // New API format: document type is accessible if it exists in permissions
      return true;
    } catch (err) {
      logger.error('[ACL] isDocumentTypeAccessible error:', err);
    }
    return false;
  },

  /**
   * Fetch ACLs from server and cache them
   * Automatically retries on transient errors (5xx status codes) with exponential backoff
   * @param force - If true, bypasses cache and forces a fresh fetch
   */
  async fetch(force = false) {
    // Skip fetch if cache exists and not forced
    const cachedAcls = Acl_Cache.get();
    if (!force && cachedAcls && Object.keys(cachedAcls).length > 0) {
      logger.debug('[ACL] Using cached ACLs, skipping fetch');
      // Group ids may not have been loaded yet (e.g. cache survived a reload);
      // ensure they are present without re-fetching the permissions.
      await this.fetchUserGroups();
      return;
    }

    // De-dup concurrent fetches: if a fetch is already in flight (e.g.
    // PrepareDashboard + dataCache both kicked one off on login), join
    // the existing promise instead of starting a parallel request.
    // Skipped when force=true so an explicit refresh always runs.
    if (!force && _inflightFetch) {
      logger.debug('[ACL] Joining in-flight fetch (dedup)');
      return _inflightFetch;
    }

    const work = (async () => {
      try {
        if (!hasValidOrgId()) {
          // Race condition - setUserSession() may not have completed yet
          await new Promise(resolve => setTimeout(resolve, 300));

          if (!hasValidOrgId()) {
            const error = {
              success: false,
              message:
                'Cannot fetch ACLs: Organization ID is missing. Please log in again.',
            };
            logger.error('[ACL] Fetch aborted: Missing organization ID');
            throw error;
          }
        }

        // Fetch ACLs with automatic retry on transient errors (5xx)
        const { userApiService } = await import('src/services');
        const response = await retryWithBackoff(
          () => userApiService.getUserAcls(),
          {
            maxAttempts: 3,
            initialDelay: 1000,
            maxDelay: 5000,
            backoffMultiplier: 2,
            shouldRetry: (error) => {
              // Retry on 500-level errors (transient server errors)
              const status = error?.response?.status || error?.status;
              const shouldRetry = status >= 500 && status < 600;

              if (shouldRetry) {
                logger.warn(`[ACL] Transient error (${status}), will retry`);
              } else if (status) {
                logger.error(`[ACL] Non-retryable error (${status}), not retrying`);
              }

              return shouldRetry;
            },
          }
        );

        if (!response || response.success === false) {
          logger.error('[ACL] API returned error response:', response);
          throw response;
        }
        const user_acls = Acl_Formatter.formatServerAClsResponse(response);
        Acl_Cache.save(user_acls);
        logger.debug('[ACL] ACLs fetched and cached successfully');

        // Load the user's group memberships into the same cache so the
        // assignee check can resolve group-assigned documents. force mirrors
        // the ACL refresh so group changes are picked up.
        await this.fetchUserGroups(force);
      } catch (err) {
        logger.error('[ACL] Fetch error after retries:', err);

        if (err && typeof err === 'object' && 'message' in err) {
          const errorMessage = String((err as any).message || '');
          if (
            errorMessage.includes('X-ORG-ID') ||
            errorMessage.includes('Organization id is required') ||
            errorMessage.includes('Organization ID is missing')
          ) {
            logger.warn('[ACL] Missing organization ID - letting router guard handle');
          }
        }
        throw err;
      }
    })();

    if (!force) {
      _inflightFetch = work.finally(() => {
        // Always clear so next call (after cache TTL or explicit force)
        // can fire a fresh request.
        _inflightFetch = null;
      });
      return _inflightFetch;
    }

    // force=true: don't latch the promise so a concurrent non-forced
    // caller doesn't accidentally inherit a force-only result.
    return work;
  },

  /**
   * Force refresh ACLs from server (bypasses cache)
   * Use this when user's group membership changes
   */
  async refresh() {
    return this.fetch(true);
  },

  async get() {
    let acls = Acl_Cache.get();
    if (!acls) {
      await this.fetch(true); // Force fetch if no cache
      acls = Acl_Cache.get();
    }
    return acls as dynamicObj;
  },

  /**
   * Check if ACLs are already cached
   */
  isCached(): boolean {
    const cachedAcls = Acl_Cache.get();
    return cachedAcls && Object.keys(cachedAcls).length > 0;
  },

  /**
   * Clear cached ACLs (useful on logout)
   */
  clear() {
    Acl_Cache.save({});
  },

  // ============================================
  // Bulk/Multi-Document Permission Helpers
  // ============================================

  /**
   * Check if user can perform an action on a list of documents
   * Useful for multi-select operations in grids
   * @param action - The action to check (delete, edit, mass_update, etc.)
   * @param documents - Array of document objects
   * @returns boolean - true if user can perform action on ALL documents
   */
  canPerformOnAll(action: string, documents: dynamicObj[]): boolean {
    if (!documents || documents.length === 0) {
      return this.hasAccess(action, {});
    }
    return documents.every(doc => this.hasAccess(action, doc));
  },

  /**
   * Check if user can perform an action on ANY of the documents
   * @param action - The action to check
   * @param documents - Array of document objects
   * @returns boolean - true if user can perform action on at least one document
   */
  canPerformOnAny(action: string, documents: dynamicObj[]): boolean {
    if (!documents || documents.length === 0) {
      return this.hasAccess(action, {});
    }
    return documents.some(doc => this.hasAccess(action, doc));
  },

  /**
   * Check if user can delete documents (for multi-select delete)
   * @param documents - Array of document objects (can be empty)
   * @returns boolean
   */
  canDelete(documents: dynamicObj[] = []): boolean {
    return this.canPerformOnAny('delete', documents);
  },

  /**
   * Check if user can edit documents (for merge, assign operations)
   * @param documents - Array of document objects (can be empty)
   * @returns boolean
   */
  canEdit(documents: dynamicObj[] = []): boolean {
    return this.canPerformOnAny('edit', documents);
  },

  /**
   * Check if user can perform mass update (for restart operations)
   * @param documents - Array of document objects (can be empty)
   * @returns boolean
   */
  canMassUpdate(documents: dynamicObj[] = []): boolean {
    return this.canPerformOnAny('mass_update', documents);
  },

  // ============================================
  // Single Document Permission Helpers
  // ============================================

  /**
   * Check if user can view a document
   * @param document - Document object
   * @returns boolean
   */
  canView(document: dynamicObj = {}): boolean {
    return this.hasAccess('view', document);
  },

  /**
   * Check if user can edit a single document
   * @param document - Document object
   * @returns boolean
   */
  canEditDoc(document: dynamicObj = {}): boolean {
    return this.hasAccess('edit', document);
  },

  /**
   * Check if user can delete a single document
   * @param document - Document object
   * @returns boolean
   */
  canDeleteDoc(document: dynamicObj = {}): boolean {
    return this.hasAccess('delete', document);
  },

  /**
   * Check if user can update/edit a single document
   * Note: API uses 'edit' action for update permissions
   * @param document - Document object
   * @returns boolean
   */
  canUpdate(document: dynamicObj = {}): boolean {
    return this.hasAccess('edit', document);
  },

  /**
   * Check if user can perform first approval on a document
   * @param document - Document object
   * @returns boolean
   */
  canFirstApproval(document: dynamicObj = {}): boolean {
    return this.hasAccess('first_approval', document);
  },

  /**
   * Check if user can perform second approval on a document
   * @param document - Document object
   * @returns boolean
   */
  canSecondApproval(document: dynamicObj = {}): boolean {
    return this.hasAccess('second_approval', document);
  },

  /**
   * Check if user can unlock a document
   * @param document - Document object
   * @returns boolean
   */
  // canUnlockDocument(document: dynamicObj = {}): boolean {
  //   return this.hasAccess('unlock_document', document);
  // },

  /**
   * Check if user can list documents of this type
   * @param document - Document object
   * @returns boolean
   */
  canList(document: dynamicObj = {}): boolean {
    return this.hasAccess('list', document);
  },
};
